import { Either } from "@jawbreaker/either";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { getNextElementByDepth, getPreviousElementByDepth, } from "./getElementByDepth";
import { getElements } from "./getElements";
export var usePosition = function (currentPos, children) {
    var ref = useRef(null);
    var _a = useState(null), prevElement = _a[0], setPrevElement = _a[1];
    var _b = useState(null), nextElement = _b[0], setNextElement = _b[1];
    var _c = useState(0), space = _c[0], setSpace = _c[1];
    useEffect(function () {
        var result = getElements(ref, currentPos);
        if (Either.isLeft(result))
            return;
        var _a = result.right, parent = _a.parent, children = _a.children, first = _a.first, last = _a.last, goTo = _a.goTo;
        var update = function () {
            var containerDimensions = parent.getBoundingClientRect();
            var newSpace = getPaddingLeft(parent);
            if (newSpace !== space)
                setSpace(newSpace);
            var _a = getNavigation(containerDimensions, first.getBoundingClientRect(), last.getBoundingClientRect(), children.filter(function (child) {
                return isVisible(containerDimensions, child.getBoundingClientRect(), newSpace);
            }), newSpace), prev = _a[0], next = _a[1];
            setPrevElement(prev);
            setNextElement(next);
        };
        if (goTo)
            scrollToElement(goTo, space);
        update();
        var debouncedUpdate = debounce(update, 50, {
            leading: true,
            trailing: true,
        });
        parent.addEventListener("scroll", debouncedUpdate, {
            passive: true,
        });
        window.addEventListener("resize", debouncedUpdate);
        return function () {
            parent.removeEventListener("scroll", debouncedUpdate);
            window.removeEventListener("resize", debouncedUpdate);
        };
    }, [ref, space, children]);
    var scrollToElement = useCallback(function (element, space) {
        var container = ref.current;
        if (container === null)
            return;
        // Check if user prefers reduced motion
        var prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        container.scroll({
            left: element.offsetLeft - space,
            behavior: prefersReducedMotion ? "auto" : "smooth",
        });
    }, [ref]);
    var scrollRight = useCallback(function () {
        return nextElement ? scrollToElement(nextElement, space) : undefined;
    }, [scrollToElement, nextElement, space]);
    var scrollLeft = useCallback(function () {
        prevElement ? scrollToElement(prevElement, space) : undefined;
    }, [scrollToElement, prevElement, space]);
    return {
        ref: ref,
        hasItemsOnLeft: prevElement !== null,
        hasItemsOnRight: nextElement !== null,
        scrollRight: scrollRight,
        scrollLeft: scrollLeft,
    };
};
/**
 * Children can be sized based on a relative portain of the container.
 * This may cause their right and left values to be non whole pixels.
 * So we need to use Math.floor and Math.ceil to make sure a child.right value
 * of 200.44 still fits inside a parent.right of 200.
 */
var isVisible = function (parent, child, space) {
    return (Math.ceil(child.left) >= Math.floor(parent.left) + space &&
        Math.floor(child.right) <= Math.ceil(parent.right) - space);
};
var getPaddingLeft = function (element) {
    var value = window
        .getComputedStyle(element)
        .getPropertyValue("padding-left");
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
};
var getNavigation = function (parent, first, last, visibleElements, space) {
    if (visibleElements.length <= 0)
        return [null, null];
    var prevElement = isVisible(parent, first, space)
        ? null
        : getPreviousElementByDepth(visibleElements.length, visibleElements[0]);
    var nextElement = isVisible(parent, last, space)
        ? null
        : getNextElementByDepth(visibleElements.length, visibleElements[0]);
    return [prevElement, nextElement];
};
